import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppConfig } from "../Configs/AppConfig";
import {
  acquireMqttLiveSocket,
  releaseMqttLiveSocket,
  subscribeMqttLiveConnection,
} from "./mqttLiveSocket";
import { _get } from "../Services/baseApi";
import {
  deviceLidarTelemetryChartUrl,
  deviceLidarTelemetryUrl,
  locationLidarTelemetryChartUrl,
  locationLidarTelemetryUrl,
} from "../Services/endpoints";
import {
  liveTelemetryLocationIds,
  resolveTelemetryDisplay,
  telemetryRecordKey,
} from "../Utils/lidarTelemetryFields";
import { normalizeMqttId } from "../Utils/telemetryConnectivityUtils";
import {
  countLiveOnlyRows,
  mergeLiveChartTelemetryRows,
  mergeLiveTelemetryRows,
} from "../Utils/telemetryLiveMerge";
import {
  buildChartFilterApiParams,
  buildDateFilterApiParams,
  chartPeriodToDateRange,
  isDateFilterValid,
  resolveDateFilterRange,
  rowMatchesLidarDateFilter,
} from "../Utils/lidarTelemetryDateFilter";
import { rowMatchesDeviceRoleFilter } from "../Utils/deviceRoleFilterUtils";
import { SENSOR_TELEMETRY_LIVE_EVENT } from "./useDeviceSensorTelemetry";

export const LIDAR_TELEMETRY_LIVE_EVENT = "lidar_telemetry:live";

/** Max points requested for chart history (backend cap is 500). */
export const LIDAR_CHART_FETCH_LIMIT = 200;

function lidarTimeAccessor(row) {
  return row?.timestamp ?? row?.startTime;
}

export function useDeviceLidarTelemetry({
  locationId,
  deviceDbId,
  masterMqttId,
  slaveMqttId,
  page,
  limit,
  search,
  sortColumn,
  sortDirection,
  dateFilter,
  deviceRoleFilter = "",
  chartEnabled = false,
}) {
  const [rows, setRows] = useState([]);
  const [chartHistoryRows, setChartHistoryRows] = useState([]);
  const [chartPeriod, setChartPeriod] = useState(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [connectionError, setConnectionError] = useState(null);
  const [liveRows, setLiveRows] = useState([]);

  const locationIdRef = useRef(locationId);
  locationIdRef.current = locationId;

  const mqttIds = useMemo(() => {
    return [masterMqttId, slaveMqttId]
      .map((id) => id?.trim())
      .filter(Boolean);
  }, [masterMqttId, slaveMqttId]);

  const mqttIdSet = useMemo(
    () => new Set(mqttIds.map((id) => normalizeMqttId(id)).filter(Boolean)),
    [mqttIds],
  );
  const mqttIdSetRef = useRef(mqttIdSet);
  mqttIdSetRef.current = mqttIdSet;

  const dateRange = useMemo(
    () =>
      resolveDateFilterRange(
        dateFilter?.preset,
        dateFilter?.fromDate,
        dateFilter?.toDate,
        dateFilter?.fromTime,
        dateFilter?.toTime,
      ),
    [
      dateFilter?.preset,
      dateFilter?.fromDate,
      dateFilter?.toDate,
      dateFilter?.fromTime,
      dateFilter?.toTime,
    ],
  );

  const dateApiParams = useMemo(
    () => buildDateFilterApiParams(dateFilter),
    [dateFilter],
  );

  const chartApiParams = useMemo(
    () => buildChartFilterApiParams(dateFilter),
    [dateFilter],
  );

  const filterReady = isDateFilterValid(dateFilter);

  const matchesRowFilters = useCallback(
    (row) =>
      rowMatchesLidarDateFilter(row, dateRange) &&
      rowMatchesDeviceRoleFilter(row, deviceRoleFilter),
    [dateRange, deviceRoleFilter],
  );

  const canPrependLive =
    page === 1 &&
    sortDirection === "DESC" &&
    sortColumn === "timestamp";

  const canPrependLiveChart = chartEnabled;

  const fetchHistory = useCallback(async ({ silent = false } = {}) => {
    const listUrl = locationId
      ? locationLidarTelemetryUrl(locationId)
      : deviceDbId
        ? deviceLidarTelemetryUrl(deviceDbId)
        : null;
    if (!listUrl || !filterReady) return;

    if (!silent) setIsLoading(true);
    try {
      const response = await _get(listUrl, {
        page,
        limit,
        search: search || undefined,
        sortColumn,
        sortDirection,
        ...(deviceRoleFilter ? { deviceRole: deviceRoleFilter } : {}),
        ...dateApiParams,
      });
      const result = response?.data?.result ?? {};
      const data = result.data ?? [];
      setRows(data);
      setTotal(result.total ?? 0);
      if (!silent) setLiveRows([]);
    } catch {
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [
    locationId,
    deviceDbId,
    page,
    limit,
    search,
    sortColumn,
    sortDirection,
    deviceRoleFilter,
    dateApiParams,
    filterReady,
  ]);

  const fetchChartHistory = useCallback(async ({ silent = false } = {}) => {
    const chartUrl = locationId
      ? locationLidarTelemetryChartUrl(locationId)
      : deviceDbId
        ? deviceLidarTelemetryChartUrl(deviceDbId)
        : null;
    if (!chartUrl || !chartEnabled || !filterReady) return;

    if (!silent) setIsChartLoading(true);
    try {
      const response = await _get(chartUrl, {
        search: search || undefined,
        ...(deviceRoleFilter ? { deviceRole: deviceRoleFilter } : {}),
        ...chartApiParams,
      });
      const result = response?.data?.result ?? {};
      const data = result.points ?? [];
      setChartHistoryRows(data);
      setChartPeriod(result.period ?? null);
      if (!silent) setLiveRows([]);
    } catch {
      if (!silent) {
        setChartHistoryRows([]);
        setChartPeriod(null);
      }
    } finally {
      if (!silent) setIsChartLoading(false);
    }
  }, [locationId, deviceDbId, chartEnabled, search, deviceRoleFilter, chartApiParams, filterReady]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    fetchChartHistory();
  }, [fetchChartHistory]);

  useEffect(() => {
    const url = AppConfig.SOCKET_URL;
    if (!locationId && mqttIds.length === 0) {
      setConnectionStatus("disconnected");
      return undefined;
    }
    if (!url) {
      setConnectionStatus("error");
      setConnectionError("MQTT live socket URL is not configured");
      return undefined;
    }

    setConnectionStatus("connecting");
    setConnectionError(null);

    const socket = acquireMqttLiveSocket(url);
    if (!socket) return undefined;

    const unsubscribeConnection = subscribeMqttLiveConnection(
      (status, error) => {
        setConnectionStatus(status);
        setConnectionError(error);
      },
    );

    const onLidarLive = (payload) => {
      const matchesDevice =
        payload?.deviceId &&
        mqttIdSetRef.current.has(normalizeMqttId(payload.deviceId));
      const matchesLocation =
        locationIdRef.current &&
        liveTelemetryLocationIds(payload).includes(
          String(locationIdRef.current),
        );
      if (!matchesDevice && !matchesLocation) {
        return;
      }

      const display = resolveTelemetryDisplay(payload);
      const hasLidar =
        display.confirmedLevelAvgCm != null ||
        display.waterLevelAvgCm != null ||
        display.confirmedDistAvgCm != null ||
        display.blinkerActive === true ||
        display.blinkerActive === false;
      if (!hasLidar) return;

      const normalized = {
        ...display,
        id: payload.id,
        topic: payload.topic,
        receivedAt: payload.receivedAt,
        _isNew: true,
      };

      setLiveRows((prev) => {
        const key = telemetryRecordKey(normalized);
        if (prev.some((row) => telemetryRecordKey(row) === key)) {
          return prev;
        }
        return [normalized, ...prev];
      });
    };

    socket.on(LIDAR_TELEMETRY_LIVE_EVENT, onLidarLive);
    socket.on(SENSOR_TELEMETRY_LIVE_EVENT, onLidarLive);

    return () => {
      socket.off(LIDAR_TELEMETRY_LIVE_EVENT, onLidarLive);
      socket.off(SENSOR_TELEMETRY_LIVE_EVENT, onLidarLive);
      unsubscribeConnection();
      releaseMqttLiveSocket();
    };
  }, [locationId, mqttIds.join("|")]);

  useEffect(() => {
    setLiveRows([]);
    setChartHistoryRows([]);
    setChartPeriod(null);
  }, [
    locationId,
    deviceDbId,
    dateFilter?.preset,
    dateFilter?.fromDate,
    dateFilter?.toDate,
    dateFilter?.fromTime,
    dateFilter?.toTime,
    deviceRoleFilter,
  ]);

  useEffect(() => {
    if (!liveRows.some((row) => row._isNew)) return undefined;

    const timer = setTimeout(() => {
      setLiveRows((prev) => prev.map((row) => ({ ...row, _isNew: false })));
    }, 2500);

    return () => clearTimeout(timer);
  }, [liveRows]);

  const displayRows = useMemo(() => {
    const historyRows = dateRange
      ? rows.filter((row) => rowMatchesLidarDateFilter(row, dateRange))
      : rows;

    if (!canPrependLive) return historyRows;

    return mergeLiveTelemetryRows({
      historyRows,
      liveRows,
      limit,
      dedupeKey: telemetryRecordKey,
      matchesDateFilter: matchesRowFilters,
      dateRange,
      timeAccessor: lidarTimeAccessor,
    });
  }, [rows, liveRows, canPrependLive, limit, matchesRowFilters, dateRange]);

  const displayTotal = useMemo(() => {
    if (!canPrependLive) return total;

    return (
      total +
      countLiveOnlyRows({
        historyRows: rows,
        liveRows,
        dedupeKey: telemetryRecordKey,
        matchesDateFilter: matchesRowFilters,
        dateRange,
        timeAccessor: lidarTimeAccessor,
      })
    );
  }, [total, rows, liveRows, canPrependLive, matchesRowFilters, dateRange]);

  const displayChartRows = useMemo(() => {
    if (!chartEnabled) return [];

    if (!canPrependLiveChart) return chartHistoryRows;

    return mergeLiveChartTelemetryRows({
      historyRows: chartHistoryRows,
      liveRows,
      dedupeKey: telemetryRecordKey,
      matchesDateFilter: matchesRowFilters,
      dateRange,
      timeAccessor: lidarTimeAccessor,
    });
  }, [chartHistoryRows, liveRows, canPrependLiveChart, chartEnabled, matchesRowFilters, dateRange]);

  const chartDateRange = useMemo(() => {
    const fromApi = chartPeriodToDateRange(chartPeriod);
    if (fromApi) {
      return { ...fromApi, periodLabel: dateRange?.periodLabel };
    }
    return dateRange;
  }, [chartPeriod, dateRange]);

  return {
    rows: displayRows,
    chartRows: displayChartRows,
    total: displayTotal,
    isLoading,
    isChartLoading,
    connectionStatus,
    connectionError,
    refetch: fetchHistory,
    refetchChart: fetchChartHistory,
    dateRange,
    chartDateRange,
    filterReady,
  };
}
